/**
 * CLIKKER — Secure Buyer Order & Delivery
 * Asset: clikker-order.js
 * Version: 1.1.0
 *
 * Page:
 * - page.clikker-order.liquid
 *
 * Security:
 * - Never reads private orders directly from Firestore.
 * - Sends order ID + buyer token to a trusted Firebase HTTPS function.
 * - Renders only the sanitized order payload returned by the backend.
 */
(function () {
  "use strict";

  var VERSION = "1.1.0";
  var REQUEST_TIMEOUT_MS = 25000;

  var VERIFY_ORDER_URL =
    "https://asia-south1-creator-dashboard-7273e.cloudfunctions.net/getSecureBuyerOrder";

  var state = {
    orderId: "",
    token: "",
    order: null,
    loading: false
  };

  var el = {};

  var PRODUCT_ICONS = {
    creator_reply: "💬",
    scheduled_call: "📞",
    video_message: "🎥",
    social_promotion: "📱",
    digital_download: "📘",
    content_access: "🔒",
    community_access: "👥",
    custom_service: "✨"
  };

  var PRODUCT_LABELS = {
    creator_reply: "Personal Reply",
    scheduled_call: "1-to-1 Call",
    video_message: "Video Message",
    social_promotion: "Social Promotion",
    digital_download: "Digital Download",
    content_access: "Exclusive Content",
    community_access: "Paid Community",
    custom_service: "Custom Service"
  };

  function byId(id) {
    return document.getElementById(id);
  }

  function clean(value) {
    return String(value == null ? "" : value).trim();
  }

  function esc(value) {
    return String(value == null ? "" : value).replace(/[&<>"']/g, function (character) {
      return {
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#039;"
      }[character];
    });
  }

  function firstValue() {
    for (var i = 0; i < arguments.length; i += 1) {
      var value = arguments[i];
      if (value !== undefined && value !== null && value !== "") {
        return value;
      }
    }

    return "";
  }

  function safeURL(value, options) {
    var text = clean(value);
    options = options || {};

    if (!text) return "";

    try {
      var url = new URL(text, window.location.origin);
      var protocols = options.protocols || ["https:", "http:"];

      if (protocols.indexOf(url.protocol) === -1) return "";
      if (options.sameOrigin === true && url.origin !== window.location.origin) return "";

      return url.href;
    } catch (_) {
      return "";
    }
  }

  function safeImageURL(value) {
    return safeURL(value, { protocols: ["https:", "http:"] });
  }

  function safeDeliveryURL(value) {
    return safeURL(value, { protocols: ["https:"] });
  }

  function normalizeType(value) {
    return clean(value)
      .toLowerCase()
      .replace(/[\s-]+/g, "_")
      .replace(/[^a-z0-9_]/g, "")
      .replace(/^_+|_+$/g, "");
  }

  function humanize(value) {
    return clean(value)
      .replace(/[_-]+/g, " ")
      .replace(/\b\w/g, function (character) {
        return character.toUpperCase();
      });
  }

  function withTimeout(promise, timeoutMs, message) {
    var timer = null;

    var timeout = new Promise(function (_, reject) {
      timer = setTimeout(function () {
        reject(new Error(message || "The request took too long."));
      }, timeoutMs);
    });

    return Promise.race([promise, timeout]).finally(function () {
      if (timer) {
        clearTimeout(timer);
      }
    });
  }

  function money(value, currency) {
    try {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: currency || "INR",
        maximumFractionDigits: 0
      }).format(Number(value || 0));
    } catch (_) {
      return "₹" + Number(value || 0).toLocaleString("en-IN");
    }
  }

  function dateValue(value) {
    if (!value) {
      return null;
    }

    try {
      if (typeof value === "object" && value.seconds) {
        return new Date(Number(value.seconds) * 1000);
      }

      var date = new Date(value);

      return Number.isNaN(date.getTime()) ? null : date;
    } catch (_) {
      return null;
    }
  }

  function dateText(value) {
    var date = dateValue(value);

    if (!date) {
      return "—";
    }

    return new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    }).format(date);
  }

  function typeOf(order) {
    order = order || {};
    return normalizeType(firstValue(
      order.workflowType,
      order.workflow_type,
      order.productType,
      order.product_type,
      order.fulfilmentType,
      order.fulfilment_type,
      order.fulfillmentType,
      order.fulfillment_type,
      order.product && firstValue(
        order.product.workflowType,
        order.product.workflow_type,
        order.product.type
      ),
      "creator_reply"
    ));
  }

  function typeLabel(type) {
    return PRODUCT_LABELS[type] || humanize(type);
  }

  function productIcon(type) {
    return PRODUCT_ICONS[type] || "✨";
  }

  function cache() {
    [
      "clikkerOrderApp",
      "clikkerOrderLoading",
      "clikkerOrderLoadingMessage",
      "clikkerOrderError",
      "clikkerOrderErrorTitle",
      "clikkerOrderErrorMessage",
      "clikkerOrderRetry",
      "clikkerOrderErrorHome",
      "clikkerOrderMain",
      "clikkerOrderAlert",
      "clikkerOrderTitle",
      "clikkerOrderStatus",
      "clikkerOrderNumber",
      "clikkerOrderShopifyNumber",
      "clikkerOrderShopifySeparator",
      "clikkerOrderRefresh",
      "clikkerOrderCreatorAvatar",
      "clikkerOrderCreatorName",
      "clikkerOrderOpenStore",
      "clikkerOrderProductMedia",
      "clikkerOrderProductType",
      "clikkerOrderProductTitle",
      "clikkerOrderProductDescription",
      "clikkerOrderPaymentStatus",
      "clikkerOrderAmount",
      "clikkerOrderDeliveryDeadline",
      "clikkerOrderBuyerName",
      "clikkerOrderInstagram",
      "clikkerOrderPreferredFormat",
      "clikkerOrderQuestion",
      "clikkerOrderExtraRequirements",
      "clikkerOrderDeliveryCard",
      "clikkerOrderDeliverySubtitle",
      "clikkerOrderDeliveryPending",
      "clikkerOrderPendingTitle",
      "clikkerOrderPendingMessage",
      "clikkerOrderDeliveryReady",
      "clikkerOrderDeliveredAt",
      "clikkerOrderReplyWrap",
      "clikkerOrderReplyText",
      "clikkerOrderOpenDelivery",
      "clikkerOrderDownloadDelivery",
      "clikkerOrderDeliveryNote",
      "clikkerOrderDeliveryExpiry",
      "clikkerOrderSubtotal",
      "clikkerOrderPlatformFee",
      "clikkerOrderTax",
      "clikkerOrderTotal",
      "clikkerOrderTimeline",
      "clikkerOrderContactCreator",
      "clikkerOrderContactSupport"
    ].forEach(function (id) {
      el[id] = byId(id);
    });
  }

  function showLoading(message) {
    state.loading = true;

    el.clikkerOrderLoading.hidden = false;
    el.clikkerOrderError.hidden = true;
    el.clikkerOrderMain.hidden = true;

    if (message) {
      el.clikkerOrderLoadingMessage.textContent = message;
    }

    if (el.clikkerOrderRefresh) {
      el.clikkerOrderRefresh.disabled = true;
      el.clikkerOrderRefresh.setAttribute("aria-busy", "true");
    }

    if (el.clikkerOrderApp) {
      el.clikkerOrderApp.dataset.state = "loading";
    }
  }

  function showError(title, message) {
    state.loading = false;

    el.clikkerOrderLoading.hidden = true;
    el.clikkerOrderMain.hidden = true;
    el.clikkerOrderError.hidden = false;

    el.clikkerOrderErrorTitle.textContent = title;
    el.clikkerOrderErrorMessage.textContent = message;

    if (el.clikkerOrderApp) {
      el.clikkerOrderApp.dataset.state = "error";
    }

    if (el.clikkerOrderRefresh) {
      el.clikkerOrderRefresh.disabled = false;
      el.clikkerOrderRefresh.removeAttribute("aria-busy");
    }
  }

  function showMain() {
    state.loading = false;

    el.clikkerOrderLoading.hidden = true;
    el.clikkerOrderError.hidden = true;
    el.clikkerOrderMain.hidden = false;

    if (el.clikkerOrderApp) {
      el.clikkerOrderApp.dataset.state = "ready";
    }

    if (el.clikkerOrderRefresh) {
      el.clikkerOrderRefresh.disabled = false;
      el.clikkerOrderRefresh.removeAttribute("aria-busy");
    }
  }

  function showAlert(message, type) {
    el.clikkerOrderAlert.className =
      "cko-alert is-visible cko-alert--" + (type || "warning");
    el.clikkerOrderAlert.textContent = message;
  }

  function clearAlert() {
    el.clikkerOrderAlert.className = "cko-alert";
    el.clikkerOrderAlert.textContent = "";
  }

  function readAccessParams() {
    var url = new URL(window.location.href);
    var orderId = clean(url.searchParams.get("order"));
    var token = clean(url.searchParams.get("token"));

    try {
      if (!orderId) orderId = clean(sessionStorage.getItem("clikkerSecureOrderId"));
      if (!token) token = clean(sessionStorage.getItem("clikkerSecureOrderToken"));
    } catch (_) {}

    return { orderId: orderId, token: token };
  }

  function protectAccessToken(orderId, token) {
    try {
      sessionStorage.setItem("clikkerSecureOrderId", orderId);
      sessionStorage.setItem("clikkerSecureOrderToken", token);

      var url = new URL(window.location.href);
      url.searchParams.delete("token");
      window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
    } catch (_) {}
  }

  async function fetchSecureOrder(orderId, token) {
    var response = await withTimeout(
      fetch(VERIFY_ORDER_URL, {
        method: "POST",
        mode: "cors",
        cache: "no-store",
        credentials: "omit",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          orderId: orderId,
          token: token
        })
      }),
      REQUEST_TIMEOUT_MS,
      "Secure order verification took too long."
    );

    var result = null;

    try {
      result = await response.json();
    } catch (_) {
      result = null;
    }

    if (!response.ok || !result || result.success !== true || !result.order) {
      var message = clean(
        firstValue(
          result && result.message,
          response.status === 404
            ? "This order could not be found."
            : "",
          response.status === 401 || response.status === 403
            ? "This secure order link is invalid or has expired."
            : "",
          "We could not open this order."
        )
      );

      throw new Error(message);
    }

    return result.order;
  }

  function orderStatusKey(order) {
    var payment = clean(firstValue(order.paymentStatus, order.payment_status, "pending")).toLowerCase();
    var orderStatus = clean(firstValue(order.orderStatus, order.order_status, "")).toLowerCase();
    var fulfilment = clean(firstValue(
      order.fulfilmentStatus,
      order.fulfilment_status,
      order.fulfillmentStatus,
      order.fulfillment_status,
      "not_started"
    )).toLowerCase();

    if (["cancelled", "canceled"].indexOf(orderStatus) >= 0 || ["cancelled", "canceled"].indexOf(fulfilment) >= 0) return "cancelled";
    if (["refunded", "partially_refunded"].indexOf(payment) >= 0 || orderStatus === "refunded") return "refunded";
    if (payment !== "paid" && payment !== "captured" && payment !== "completed") return "payment_pending";
    if (["delivered", "completed", "fulfilled"].indexOf(fulfilment) >= 0) return "delivered";
    if (["in_progress", "started", "processing", "accepted"].indexOf(fulfilment) >= 0) return "in_progress";
    return "confirmed";
  }

  function orderStatusLabel(order) {
    var labels = {
      payment_pending: "Payment pending",
      confirmed: "Confirmed",
      in_progress: "In progress",
      delivered: "Delivered",
      cancelled: "Cancelled",
      refunded: "Refunded"
    };
    return labels[orderStatusKey(order)] || "Confirmed";
  }

  function setOrderStatus(order) {
    var status = orderStatusKey(order);
    var className = {
      payment_pending: "cko-status--pending",
      confirmed: "cko-status--paid",
      in_progress: "cko-status--progress",
      delivered: "cko-status--delivered",
      cancelled: "cko-status--cancelled",
      refunded: "cko-status--refunded"
    }[status] || "cko-status--paid";

    el.clikkerOrderStatus.textContent = orderStatusLabel(order);
    el.clikkerOrderStatus.className = "cko-status " + className;
  }

  function renderCreator(order) {
    var creator = order.creator || {};
    var name = clean(firstValue(creator.name, creator.displayName, "Creator"));
    var image = safeImageURL(
      firstValue(
        creator.profileImage,
        creator.profileImageURL,
        creator.photoURL,
        ""
      )
    );

    el.clikkerOrderCreatorName.textContent = name;
    el.clikkerOrderCreatorAvatar.innerHTML = image
      ? '<img src="' + esc(image) + '" alt="' + esc(name) + '">'
      : esc(name.charAt(0).toUpperCase() || "C");

    var storeUrl = safeURL(
      firstValue(
        order.storeUrl,
        order.store && order.store.url,
        order.storeSlug
          ? (window.CLIKKER_PUBLIC_STORE_ROUTE || "/pages/c?creator=") + encodeURIComponent(order.storeSlug)
          : ""
      )
    );

    if (storeUrl) {
      el.clikkerOrderOpenStore.href = storeUrl;
      el.clikkerOrderOpenStore.hidden = false;
    } else {
      el.clikkerOrderOpenStore.hidden = true;
    }

    var creatorEmail = clean(
      firstValue(
        creator.supportEmail,
        creator.email,
        order.supportEmail,
        ""
      )
    );

    if (creatorEmail) {
      el.clikkerOrderContactCreator.href =
        "mailto:" +
        encodeURIComponent(creatorEmail) +
        "?subject=" +
        encodeURIComponent("Clikker order " + clean(order.orderNumber));
      el.clikkerOrderContactCreator.hidden = false;
    } else {
      el.clikkerOrderContactCreator.hidden = true;
    }
  }

  function renderProduct(order) {
    var product = order.product || {};
    var type = typeOf(order);
    var title = clean(
      firstValue(product.title, order.productTitle, typeLabel(type))
    );
    var description = clean(
      firstValue(
        product.description,
        product.shortDescription,
        order.productDescription,
        ""
      )
    );
    var image = safeImageURL(
      firstValue(
        product.coverImage,
        product.thumbnail,
        order.productImage,
        ""
      )
    );

    el.clikkerOrderTitle.textContent = title;
    el.clikkerOrderProductType.textContent = typeLabel(type);
    el.clikkerOrderProductTitle.textContent = title;
    el.clikkerOrderProductDescription.textContent =
      description || "A personalised creator order.";

    el.clikkerOrderProductMedia.innerHTML = image
      ? '<img src="' + esc(image) + '" alt="' + esc(title) + '">'
      : esc(productIcon(type));
  }

  function requirementsOf(order) {
    var requirements = firstValue(
      order.buyerRequirements,
      order.buyer_requirements,
      order.requirements,
      order.buyer && order.buyer.requirements,
      {}
    );

    return requirements && typeof requirements === "object" && !Array.isArray(requirements)
      ? requirements
      : {};
  }

  function requirementValue(order, keys, fallback) {
    var requirements = requirementsOf(order);

    for (var i = 0; i < keys.length; i += 1) {
      var value = requirements[keys[i]];

      if (value !== undefined && value !== null && value !== "") {
        return value;
      }
    }

    return fallback || "";
  }

  function renderBuyerRequest(order) {
    var buyer = order.buyer || {};

    var buyerName = clean(
      firstValue(
        buyer.name,
        requirementValue(order, ["customer_name", "customerName", "buyer_name", "buyerName", "name"]),
        "Customer"
      )
    );

    var instagram = clean(
      requirementValue(
        order,
        ["instagram_username", "instagramUsername", "instagram", "instagram_handle", "instagramHandle"],
        "—"
      )
    );

    var preferredFormat = clean(
      requirementValue(
        order,
        [
          "preferred_reply_format",
          "preferredReplyFormat",
          "preferred_format",
          "preferredFormat",
          "reply_format",
          "replyFormat"
        ],
        "Creator can choose"
      )
    );

    var question = clean(
      requirementValue(
        order,
        ["question", "message", "question_or_message", "questionOrMessage", "customer_question", "customerQuestion"],
        "No message provided."
      )
    );

    el.clikkerOrderBuyerName.textContent = buyerName;
    el.clikkerOrderInstagram.textContent = instagram;
    el.clikkerOrderPreferredFormat.textContent = preferredFormat;
    el.clikkerOrderQuestion.textContent = question;

    var ignored = {
      customer_name: true,
      customerName: true,
      buyer_name: true,
      buyerName: true,
      name: true,
      instagram_username: true,
      instagramUsername: true,
      instagram: true,
      instagramHandle: true,
      preferred_reply_format: true,
      preferredReplyFormat: true,
      preferred_format: true,
      preferredFormat: true,
      reply_format: true,
      replyFormat: true,
      preferred_reply_format: true,
      question: true,
      message: true,
      question_or_message: true,
      questionOrMessage: true,
      customer_question: true,
      customerQuestion: true
    };

    var requirements = requirementsOf(order);
    var rows = [];

    Object.keys(requirements).forEach(function (key) {
      var value = requirements[key];

      if (
        ignored[key] ||
        value === undefined ||
        value === null ||
        value === ""
      ) {
        return;
      }

      if (typeof value === "object") {
        return;
      }

      rows.push(
        '<div class="cko-request__item">' +
          '<span>' +
            esc(key.replace(/([A-Z])/g, " $1").replace(/_/g, " ")) +
          '</span>' +
          '<strong>' +
            esc(value === true ? "Yes" : value === false ? "No" : humanize(String(value))) +
          '</strong>' +
        '</div>'
      );
    });

    el.clikkerOrderExtraRequirements.innerHTML = rows.join("");
  }

  function renderPayment(order) {
    var amount = order.amount || {};
    var currency = clean(firstValue(amount.currency, order.currency, "INR")).toUpperCase();

    var subtotal = Number(firstValue(
      amount.verifiedSubtotal,
      amount.shopifySubtotal,
      amount.subtotal,
      order.subtotal,
      0
    ));
    var platformFee = Number(firstValue(amount.verifiedPlatformFee, amount.platformFee, order.platformFee, 0));
    var tax = Number(firstValue(amount.verifiedTax, amount.tax, order.tax, 0));
    var total = Number(firstValue(
      amount.verifiedTotal,
      amount.shopifyTotal,
      amount.total,
      order.verifiedTotal,
      order.total,
      subtotal + platformFee + tax,
      0
    ));

    var paymentStatus = clean(firstValue(order.paymentStatus, order.payment_status, "pending")).toLowerCase();
    el.clikkerOrderPaymentStatus.textContent = humanize(paymentStatus || "pending");
    el.clikkerOrderAmount.textContent = money(total, currency);
    el.clikkerOrderSubtotal.textContent = money(subtotal, currency);
    el.clikkerOrderPlatformFee.textContent = money(platformFee, currency);
    el.clikkerOrderTax.textContent = money(tax, currency);
    el.clikkerOrderTotal.textContent = money(total, currency);
  }

  function renderDelivery(order) {
    var status = orderStatusKey(order);
    var delivery = order.delivery || {};

    var replyText = clean(
      firstValue(
        delivery.replyText,
        order.replyText,
        order.deliveryNote,
        ""
      )
    );

    var deliveryLink = safeDeliveryURL(
      firstValue(
        delivery.link,
        order.deliveryLink,
        ""
      )
    );

    var downloadURL = safeDeliveryURL(
      firstValue(
        delivery.downloadURL,
        order.downloadURL,
        order.deliveryFileURL,
        ""
      )
    );

    var deliveryNote = clean(
      firstValue(
        delivery.note,
        order.deliveryMessage,
        ""
      )
    );

    var expiresAt = firstValue(
      delivery.expiresAt,
      delivery.expiry,
      order.deliveryExpiresAt,
      order.downloadExpiresAt,
      ""
    );

    if (el.clikkerOrderDeliveryExpiry) {
      if (expiresAt) {
        el.clikkerOrderDeliveryExpiry.textContent = "Secure access expires " + dateText(expiresAt) + ".";
        el.clikkerOrderDeliveryExpiry.hidden = false;
      } else {
        el.clikkerOrderDeliveryExpiry.hidden = true;
      }
    }

    if (status === "delivered") {
      el.clikkerOrderDeliveryPending.hidden = true;
      el.clikkerOrderDeliveryReady.hidden = false;
      el.clikkerOrderDeliverySubtitle.textContent =
        "Your creator has completed this order.";

      el.clikkerOrderDeliveredAt.textContent =
        "Delivered " + dateText(order.deliveredAt);

      if (replyText) {
        el.clikkerOrderReplyText.textContent = replyText;
        el.clikkerOrderReplyWrap.hidden = false;
      } else {
        el.clikkerOrderReplyWrap.hidden = true;
      }

      if (deliveryLink) {
        el.clikkerOrderOpenDelivery.href = deliveryLink;
        el.clikkerOrderOpenDelivery.target = "_blank";
        el.clikkerOrderOpenDelivery.rel = "noopener noreferrer";
        el.clikkerOrderOpenDelivery.hidden = false;
      } else {
        el.clikkerOrderOpenDelivery.hidden = true;
      }

      if (downloadURL) {
        el.clikkerOrderDownloadDelivery.href = downloadURL;
        el.clikkerOrderDownloadDelivery.target = "_blank";
        el.clikkerOrderDownloadDelivery.rel = "noopener noreferrer";
        el.clikkerOrderDownloadDelivery.hidden = false;
      } else {
        el.clikkerOrderDownloadDelivery.hidden = true;
      }

      if (deliveryNote) {
        el.clikkerOrderDeliveryNote.textContent = deliveryNote;
        el.clikkerOrderDeliveryNote.hidden = false;
      } else {
        el.clikkerOrderDeliveryNote.hidden = true;
      }
    } else {
      el.clikkerOrderDeliveryPending.hidden = false;
      el.clikkerOrderDeliveryReady.hidden = true;

      if (status === "payment_pending") {
        el.clikkerOrderPendingTitle.textContent = "Payment confirmation pending";
        el.clikkerOrderPendingMessage.textContent = "We are waiting for secure payment confirmation. Refresh this page shortly.";
      } else if (status === "cancelled") {
        el.clikkerOrderPendingTitle.textContent = "This order was cancelled";
        el.clikkerOrderPendingMessage.textContent = "Contact Clikker support if you need help with this order.";
      } else if (status === "refunded") {
        el.clikkerOrderPendingTitle.textContent = "This order was refunded";
        el.clikkerOrderPendingMessage.textContent = "The refund status shown here is based on the verified payment record.";
      } else if (status === "in_progress") {
        el.clikkerOrderPendingTitle.textContent = "Your order is being prepared";
        el.clikkerOrderPendingMessage.textContent = "The creator has started working on your paid order.";
      } else {
        el.clikkerOrderPendingTitle.textContent = "Your order is confirmed";
        el.clikkerOrderPendingMessage.textContent = "Payment is complete. The creator will start fulfilment soon.";
      }
    }
  }

  function renderTimeline(order) {
    var rows = [
      {
        label: "Order created",
        date: order.createdAt,
        show: true
      },
      {
        label: "Payment confirmed",
        date: firstValue(order.paymentConfirmedAt, order.paidAt),
        show: clean(order.paymentStatus).toLowerCase() === "paid"
      },
      {
        label: "Creator started fulfilment",
        date: firstValue(
          order.fulfilmentStartedAt,
          order.fulfillmentStartedAt
        ),
        show: ["in_progress", "delivered"].indexOf(orderStatusKey(order)) >= 0
      },
      {
        label: "Order delivered",
        date: order.deliveredAt,
        show: orderStatusKey(order) === "delivered"
      }
    ];

    el.clikkerOrderTimeline.innerHTML = rows
      .filter(function (row) {
        return row.show;
      })
      .map(function (row) {
        return (
          "<li>" +
            "<strong>" + esc(row.label) + "</strong>" +
            "<span>" + esc(dateText(row.date)) + "</span>" +
          "</li>"
        );
      })
      .join("");
  }

  function renderOrder(order) {
    clearAlert();

    state.order = order;

    setOrderStatus(order);

    el.clikkerOrderNumber.textContent =
      clean(firstValue(order.orderNumber, order.id, "Order"));

    var shopifyNumber = clean(firstValue(
      order.shopifyOrderName,
      order.shopifyOrderNumber,
      order.shopify_order_name,
      ""
    ));

    el.clikkerOrderShopifyNumber.textContent = shopifyNumber;
    el.clikkerOrderShopifyNumber.hidden = !shopifyNumber;
    if (el.clikkerOrderShopifySeparator) {
      el.clikkerOrderShopifySeparator.hidden = !shopifyNumber;
    }

    renderCreator(order);
    renderProduct(order);
    renderBuyerRequest(order);
    renderPayment(order);
    renderDelivery(order);
    renderTimeline(order);

    el.clikkerOrderDeliveryDeadline.textContent = clean(
      firstValue(
        order.deliveryDeadlineText,
        order.deliveryDeadline
          ? dateText(order.deliveryDeadline)
          : "",
        order.deliveryText,
        "As stated"
      )
    );

    showMain();
  }

  async function boot() {
    if (state.loading) {
      return;
    }

    showLoading(
      "We are securely verifying your order and access link."
    );

    var params = readAccessParams();

    state.orderId = params.orderId;
    state.token = params.token;

    if (!state.orderId || !state.token) {
      showError(
        "Secure order link required",
        "This page needs both an order ID and secure buyer token."
      );
      return;
    }

    try {
      var order = await fetchSecureOrder(
        state.orderId,
        state.token
      );

      protectAccessToken(state.orderId, state.token);
      renderOrder(order);

      console.info("[Clikker Buyer Order] Ready", VERSION);
    } catch (error) {
      console.error("[Clikker Buyer Order]", error);

      showError(
        "Order unavailable",
        clean(error && error.message) ||
          "This secure order link is invalid or expired."
      );
    }
  }

  function bind() {
    el.clikkerOrderRetry.onclick = function () {
      boot();
    };

    if (el.clikkerOrderRefresh) {
      el.clikkerOrderRefresh.onclick = function () {
        boot();
      };
    }
  }

  function init() {
    cache();

    if (
      !el.clikkerOrderApp ||
      !el.clikkerOrderLoading ||
      !el.clikkerOrderMain
    ) {
      console.error(
        "[Clikker Buyer Order] Required page containers are missing."
      );
      return;
    }

    bind();
    boot();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, {
      once: true
    });
  } else {
    init();
  }
})();
